import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Vote, CheckCircle2, BarChart3, User, AlertCircle, Trophy, Lock } from 'lucide-react';
import { toast } from 'sonner';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const VotePage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeElection, setActiveElection] = useState(null);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchElections();
  }, []);

  const fetchElections = async () => {
    try {
      const r = await axios.get(`${API}/elections/active`);
      setElections(r.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Accès réservé aux professionnels actifs');
    }
    finally { setLoading(false); }
  };

  const openElection = async (id) => {
    try {
      const r = await axios.get(`${API}/elections/${id}`);
      setActiveElection(r.data);
      setSelected([]);
      setResults(null);
      if (r.data.has_voted) viewResults(id);
    } catch { toast.error('Erreur de chargement'); }
  };

  const toggleCandidate = (candidateId) => {
    if (activeElection?.has_voted) return;
    setSelected(prev => {
      if (prev.includes(candidateId)) return prev.filter(id => id !== candidateId);
      if (prev.length >= activeElection.max_choices) {
        toast.error(`Maximum ${activeElection.max_choices} choix`);
        return prev;
      }
      return [...prev, candidateId];
    });
  };

  const submitVote = async () => {
    if (selected.length < activeElection.min_choices) {
      return toast.error(`Sélectionnez au minimum ${activeElection.min_choices} candidat(s)`);
    }
    if (!window.confirm(`Confirmez votre vote pour ${selected.length} candidat(s) ? Cette action est définitive.`)) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/elections/${activeElection.id}/vote`, { choices: selected });
      toast.success('Vote enregistré !');
      setActiveElection(prev => ({ ...prev, has_voted: true }));
      viewResults(activeElection.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const viewResults = async (id) => {
    try {
      const r = await axios.get(`${API}/elections/${id}/results`);
      setResults(r.data);
    } catch { /* Results not available */ }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }}>
        <SiteHeader />
        <div className="max-w-md mx-auto text-center py-32 px-4">
          <Lock size={48} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Espace réservé</h2>
          <p className="text-gray-500 text-sm mb-6">Connectez-vous pour accéder aux votes</p>
          <button onClick={() => navigate('/auth')} className="px-6 py-2 bg-[#35AFE4] text-white rounded-full text-sm font-semibold">
            Se connecter
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="vote-page">
      <SEO title="Votes & Élections" description="Participez aux votes et élections de la SFOALC" />
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5f8a 50%, #35AFE4 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 pt-28 pb-14 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="vote-title">
            Votes & Élections
          </h1>
          <p className="text-white/70 text-sm sm:text-base max-w-xl mx-auto">
            Participez aux élections du bureau et aux consultations internes de la SFOALC
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#f8f9fb" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* No active election selected */}
        {!activeElection && (
          <>
            {loading ? (
              <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#35AFE4]" /></div>
            ) : elections.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <Vote size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="font-semibold text-gray-700">Aucun vote en cours</p>
                <p className="text-sm text-gray-400 mt-1">Les prochains votes apparaîtront ici</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {elections.map(e => (
                  <div key={e.id} className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() => openElection(e.id)} data-testid={`election-card-${e.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full">En cours</span>
                          <span className="text-[10px] text-gray-400">{e.type === 'election' ? 'Élection' : 'Sondage'}</span>
                        </div>
                        <h3 className="text-lg font-bold text-[#1e3a5f] group-hover:text-[#35AFE4] transition-colors" style={{ fontFamily: "'Barlow', sans-serif" }}>
                          {e.title}
                        </h3>
                        {e.description && <p className="text-sm text-gray-500 mt-1">{e.description}</p>}
                        <p className="text-xs text-gray-400 mt-2">
                          {e.candidates?.length || 0} candidats | Min {e.min_choices} - Max {e.max_choices} choix
                        </p>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        {e.has_voted ? (
                          <span className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-full text-xs font-semibold">
                            <CheckCircle2 size={14} /> Voté
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 px-3 py-1.5 bg-[#eaf6fc] text-[#35AFE4] rounded-full text-xs font-semibold">
                            <Vote size={14} /> Voter
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Active election - Voting interface */}
        {activeElection && !results && (
          <div>
            <button onClick={() => { setActiveElection(null); setResults(null); }}
              className="text-sm text-gray-500 hover:text-[#35AFE4] mb-4 flex items-center gap-1">
              Retour aux votes
            </button>
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>{activeElection.title}</h2>
                {activeElection.description && <p className="text-sm text-gray-500 mt-2">{activeElection.description}</p>}
                <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                  <span className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-[#35AFE4] rounded-full font-semibold">
                    <AlertCircle size={12} /> Min: {activeElection.min_choices} choix
                  </span>
                  <span className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-[#35AFE4] rounded-full font-semibold">
                    Max: {activeElection.max_choices} choix
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full font-semibold">
                    {selected.length} sélectionné(s)
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {(activeElection.candidates || []).map(c => {
                  const isSelected = selected.includes(c.id);
                  return (
                    <div key={c.id}
                      className={`relative rounded-xl border-2 p-5 text-center cursor-pointer transition-all duration-200 ${isSelected ? 'border-[#35AFE4] bg-[#eaf6fc] shadow-md' : 'border-gray-200 hover:border-[#35AFE4]/30 hover:bg-gray-50'}`}
                      onClick={() => toggleCandidate(c.id)}
                      data-testid={`candidate-${c.id}`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2"><CheckCircle2 size={20} className="text-[#35AFE4]" /></div>
                      )}
                      <div className="w-20 h-20 rounded-full mx-auto mb-3 overflow-hidden bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
                        {c.photo_url ? (
                          <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" />
                        ) : (
                          <User size={32} className="text-white" />
                        )}
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>{c.name}</h4>
                      {c.description && <p className="text-xs text-gray-500 mt-2 leading-relaxed">{c.description}</p>}
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 text-center">
                <button onClick={submitVote} disabled={submitting || selected.length < activeElection.min_choices}
                  className="px-8 py-3 bg-[#35AFE4] text-white rounded-full font-bold text-sm hover:bg-[#1e9bd4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="submit-vote-btn">
                  {submitting ? 'Envoi en cours...' : `Voter (${selected.length}/${activeElection.max_choices})`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {activeElection && results && (
          <div>
            <button onClick={() => { setActiveElection(null); setResults(null); }}
              className="text-sm text-gray-500 hover:text-[#35AFE4] mb-4 flex items-center gap-1">
              Retour aux votes
            </button>
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8">
              <div className="text-center mb-8">
                <Trophy size={36} className="mx-auto text-[#c9a227] mb-2" />
                <h2 className="text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  Résultats - {results.election?.title}
                </h2>
                <p className="text-sm text-gray-500 mt-1">{results.total_voters} votant(s)</p>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                {results.results?.map((r, i) => {
                  const maxV = results.results[0]?.votes || 1;
                  const pct = results.total_voters > 0 ? Math.round((r.votes / results.total_voters) * 100) : 0;
                  return (
                    <div key={r.candidate_id} className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${i < 3 ? 'bg-[#eaf6fc]' : 'bg-gray-50'}`} data-testid={`public-result-${i}`}>
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        i === 0 ? 'bg-[#c9a227] text-white' : i === 1 ? 'bg-gray-400 text-white' : i === 2 ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>{i + 1}</span>
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
                        {r.photo_url ? <img src={r.photo_url} alt={r.name} className="w-full h-full object-cover" /> : <span className="text-white text-xs font-bold">{r.name[0]}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-bold text-gray-800">{r.name}</span>
                          <span className="text-xs font-bold text-[#35AFE4]">{r.votes} voix ({pct}%)</span>
                        </div>
                        <div className="h-2.5 bg-white rounded-full overflow-hidden border border-gray-100">
                          <div className="h-full rounded-full transition-all duration-700 ease-out" style={{
                            width: `${maxV > 0 ? (r.votes / maxV) * 100 : 0}%`,
                            background: i === 0 ? 'linear-gradient(90deg, #c9a227, #d4a843)' : 'linear-gradient(90deg, #35AFE4, #1e3a5f)',
                          }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default VotePage;
